"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RatingsTableCodec = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const ExpressionExpansion_1 = require("../ExpressionExpansion");
const operating_condition_modes_1 = require("../Wizard/Definitions/steps/forms/operating-condition-modes");
const boundary_facts_1 = require("./boundary-facts");
const orphan_boundaries_1 = require("./orphan-boundaries");
const ratings_table_types_1 = require("./ratings-table.types");
const signal_group_registry_1 = require("./signal-group-registry");
const stored_ratings_table_1 = require("./stored-ratings-table");
class RatingsTableCodec {
    static toRatingsTable(kind, signals, modes, boundaries, storedTable, parentRows) {
        const conditions = RatingsTableCodec.conditionsOf(modes, signals);
        const pairs = RatingsTableCodec.pairsOf(signals);
        const groups = new signal_group_registry_1.SignalGroupRegistry(storedTable?.signalGroups ?? [], signals);
        const storedRows = storedTable?.rows ?? [];
        const storedRowIds = new Set(storedRows.map((row) => row.id));
        const prefilled = [];
        for (const parentRow of parentRows) {
            if (storedRowIds.has(parentRow.id)) {
                continue;
            }
            const recorded = new Set(parentRow.boundaryIds);
            const bounds = boundaries.filter((bound) => recorded.has(bound.id) && RatingsTableCodec.isPrefillable(bound, kind));
            if (bounds.length === 0) {
                continue;
            }
            prefilled.push(RatingsTableCodec.prefilledRow(parentRow, bounds, signals, groups));
        }
        return {
            formVersion: stored_ratings_table_1.RATINGS_TABLE_FORM_VERSION,
            kind,
            signalGroups: groups.all(),
            conditions,
            pairs,
            rows: [...storedRows, ...prefilled],
        };
    }
    static toBoundaries(kind, signals, modes, boundaries, table, edits, parentRows) {
        const savedModes = [...modes];
        const modeIdByConditionId = new Map();
        for (const condition of table.conditions) {
            if (modes.some((mode) => mode.id === condition.id)) {
                modeIdByConditionId.set(condition.id, condition.id);
                continue;
            }
            if (condition.value.targetSignalId === null || condition.value.relativeSignalId === null) {
                continue;
            }
            const targetId = condition.value.targetSignalId;
            const targetName = signals.find((signal) => String(signal.id) === targetId)?.name ?? targetId;
            const mode = operating_condition_modes_1.OperatingConditionModes.toMode(condition.value, targetName);
            modeIdByConditionId.set(condition.id, mode.id);
            if (!savedModes.some((existing) => existing.id === mode.id)) {
                savedModes.push(mode);
            }
        }
        const context = {
            kind,
            signalGroups: table.signalGroups,
            conditions: table.conditions.flatMap((condition) => {
                const modeId = modeIdByConditionId.get(condition.id);
                return modeId === undefined ? [] : [{ ...condition, id: modeId }];
            }),
            signals,
            pairs: RatingsTableCodec.expansionPairs(table.pairs),
        };
        const rows = table.rows.map((row) => ({
            ...row,
            conditionIds: row.conditionIds.map((id) => modeIdByConditionId.get(id) ?? id),
        }));
        const rowsNeedingReference = orphan_boundaries_1.OrphanBoundaries.rowsNeedingReference(rows, orphan_boundaries_1.OrphanBoundaries.expandRows(rows, context));
        if (rowsNeedingReference.length > 0) {
            return { outcome: ratings_table_types_1.RatingsTableSaveOutcome.Refused, rowsNeedingReference };
        }
        const dropped = new Set([
            ...orphan_boundaries_1.OrphanBoundaries.covered(rows, context, boundaries).map((bound) => bound.id),
            ...edits.deletedBoundaryIds,
            ...(edits.dropUnstatedRowBounds === true ?
                RatingsTableCodec.rowRecordedBoundIds(kind, boundaries, parentRows)
                : []),
        ]);
        const kept = boundaries.filter((bound) => !dropped.has(bound.id));
        const minted = [];
        const mintedIds = new Set();
        const savedRows = rows.map((row) => {
            const bounds = orphan_boundaries_1.OrphanBoundaries.statedBounds([row], context);
            for (const bound of bounds) {
                if (!mintedIds.has(bound.id)) {
                    mintedIds.add(bound.id);
                    minted.push(bound);
                }
            }
            return { ...row, boundaryIds: bounds.map((bound) => bound.id) };
        });
        const parentRowById = new Map(parentRows.map((parentRow) => [parentRow.id, parentRow]));
        const rowIds = new Set(savedRows.map((row) => row.id));
        const savedBoundIds = new Set([...kept, ...minted].map((bound) => bound.id));
        const carried = parentRows.filter((parentRow) => !rowIds.has(parentRow.id) && parentRow.boundaryIds.some((id) => savedBoundIds.has(id)));
        return {
            outcome: ratings_table_types_1.RatingsTableSaveOutcome.Saved,
            boundaries: [...kept, ...minted],
            modes: savedModes,
            table: {
                formVersion: stored_ratings_table_1.RATINGS_TABLE_FORM_VERSION,
                kind,
                signalGroups: table.signalGroups,
                rows: savedRows,
            },
            parentRows: [
                ...savedRows.map((row) => RatingsTableCodec.parentRowOf(row, parentRowById.get(row.id))),
                ...carried,
            ],
        };
    }
    static expansionContext(table, signals) {
        return {
            kind: table.kind,
            signalGroups: table.signalGroups,
            conditions: table.conditions,
            signals,
            pairs: RatingsTableCodec.expansionPairs(table.pairs),
        };
    }
    static prefilledRow(parentRow, bounds, signals, groups) {
        const characteristic = boundary_facts_1.BoundaryFacts.fromBoundaries(bounds, signals)[0]?.characteristic ?? sigma_engine_shared_1.BoundaryCharacteristic.Voltage;
        const signalIds = [
            ...new Set(bounds.flatMap((bound) => (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)([...signals], bound.boundary.target)
                .map(String)
                .filter((id) => signals.some((signal) => String(signal.id) === id)))),
        ];
        const relative = characteristic === sigma_engine_shared_1.BoundaryCharacteristic.Voltage ?
            { kind: ExpressionExpansion_1.RatingsRelativeReferenceKind.Signal, signalId: null }
            : null;
        const targets = characteristic === sigma_engine_shared_1.BoundaryCharacteristic.Power ? [] : signalIds;
        const grouped = targets.length > stored_ratings_table_1.MAX_ROW_BARE_SIGNALS;
        return {
            id: parentRow.id,
            origin: ExpressionExpansion_1.RatingRowOrigin.Entered,
            characteristic,
            description: parentRow.description,
            min: parentRow.min ?? '',
            typ: parentRow.typ ?? '',
            max: parentRow.max ?? '',
            relative,
            conditionIds: RatingsTableCodec.conditionIdsOf(bounds),
            groupIds: grouped ? [groups.groupFor(targets)] : [],
            ...(grouped || targets.length === 0 ? {} : { signalIds: [...targets].sort() }),
            boundaryIds: [...parentRow.boundaryIds],
            overrides: [],
        };
    }
    static rowRecordedBoundIds(kind, boundaries, parentRows) {
        const recorded = new Set(parentRows.flatMap((parentRow) => parentRow.boundaryIds));
        return boundaries
            .filter((bound) => recorded.has(bound.id) && orphan_boundaries_1.OrphanBoundaries.isOfKind(bound, kind) && orphan_boundaries_1.OrphanBoundaries.isStatableByRow(bound))
            .map((bound) => bound.id);
    }
    static isPrefillable(bound, kind) {
        return (orphan_boundaries_1.OrphanBoundaries.isOfKind(bound, kind) &&
            orphan_boundaries_1.OrphanBoundaries.isStatableByRow(bound) &&
            bound.boundary.level === (0, ExpressionExpansion_1.kindDefaultLevel)(kind));
    }
    static conditionIdsOf(bounds) {
        const ids = new Set();
        for (const bound of bounds) {
            for (const condition of bound.conditions ?? []) {
                if (sigma_engine_shared_1.DesignModels.isModeReferenceCondition(condition) && condition.mode_state) {
                    ids.add(condition.mode);
                }
            }
        }
        return [...ids].sort();
    }
    static conditionsOf(modes, signals) {
        return modes
            .map((mode) => operating_condition_modes_1.OperatingConditionModes.fromMode(mode, signals))
            .filter((condition) => condition !== null)
            .sort((a, b) => (a.id < b.id ? -1 : 1));
    }
    static pairsOf(signals) {
        const byLegs = new Map();
        for (const signal of signals) {
            const pair = signal.diffPair;
            if (pair === undefined) {
                continue;
            }
            const positiveSignalId = pair.positiveId === '' ? null : String(pair.positiveId);
            const negativeSignalId = pair.negativeId === '' ? null : String(pair.negativeId);
            const key = `${positiveSignalId ?? ''}|${negativeSignalId ?? ''}`;
            if (!byLegs.has(key)) {
                byLegs.set(key, {
                    id: `pair_${sigma_engine_shared_1.Utils.fnv1aHash(key)}`,
                    name: pair.pairName,
                    positiveSignalId,
                    negativeSignalId,
                });
            }
        }
        return [...byLegs.entries()].sort(([a], [b]) => (a < b ? -1 : 1)).map(([, pair]) => pair);
    }
    static expansionPairs(pairs) {
        const complete = [];
        for (const pair of pairs) {
            if (pair.positiveSignalId !== null && pair.negativeSignalId !== null) {
                complete.push({ positiveId: pair.positiveSignalId, negativeId: pair.negativeSignalId });
            }
        }
        return complete;
    }
    static parentRowOf(row, previous) {
        return {
            id: row.id,
            description: row.description,
            symbol: previous?.symbol ?? null,
            min: row.min === '' ? null : row.min,
            typ: row.typ === '' ? null : row.typ,
            max: row.max === '' ? null : row.max,
            boundaryIds: row.boundaryIds,
            ...(previous?.sourceRows === undefined ? {} : { sourceRows: previous.sourceRows }),
        };
    }
}
exports.RatingsTableCodec = RatingsTableCodec;
//# sourceMappingURL=ratings-table-codec.js.map